/**
 * Unit conversion utilities for property validation.
 *
 * Conversion tables and math live in @center-platform/units — the single source
 * of truth shared with the design-system's UnitValueInputAtom (the two sides
 * previously kept separate TIME_UNITS tables and drifted). This module keeps the
 * validation-facing API: coercing raw property input into stored UnitValue shape.
 *
 * Runtime-supported categories: time, distance, speed, mass, volume,
 * temperature, area (see SUPPORTED_UNIT_CATEGORIES). Unknown units within a
 * supported category throw (fail-closed — a mis-united value would corrupt
 * canonicalValue comparisons); unsupported categories store values as-is.
 */

import type { SchemaProperty, UnitValue, UnitConfig } from 'types/schema';
import {
  getCanonicalUnit,
  normalizeUnit,
  toCanonical,
} from '@center-platform/units';

/**
 * Check if a value is already a UnitValue object
 */
export function isUnitValue(value: unknown): value is UnitValue {
  return (
    typeof value === 'object' &&
    value !== null &&
    'value' in value &&
    'unit' in value &&
    'canonicalValue' in value &&
    'canonicalUnit' in value
  );
}

/**
 * Validate that a unit is in the allowed units list
 */
function validateAllowedUnit(unit: string, allowedUnits: string[] | undefined): void {
  if (allowedUnits && allowedUnits.length > 0 && !allowedUnits.includes(unit)) {
    throw new Error(`Invalid unit '${unit}'. Allowed: ${allowedUnits.join(', ')}`);
  }
}

/**
 * Convert a raw input value to a UnitValue object
 *
 * @param input - The raw input (number, partial UnitValue, or full UnitValue)
 * @param unitConfig - The unit configuration from the schema property
 * @returns A complete UnitValue object with canonical values
 * @throws Error if the unit is not in the allowedUnits list, or is unknown
 *         within a runtime-supported category
 */
export function convertToUnitValue(
  input: number | Partial<UnitValue> | UnitValue,
  unitConfig: UnitConfig
): UnitValue {
  const { category, defaultUnit, displayPrecision = 2, allowedUnits } = unitConfig;
  const canonicalUnit = getCanonicalUnit(category);

  // Already a complete UnitValue - still validate the unit
  if (isUnitValue(input)) {
    validateAllowedUnit(input.unit, allowedUnits);
    return input;
  }

  // Plain number - use default unit
  if (typeof input === 'number') {
    const unit = normalizeUnit(defaultUnit, category);
    validateAllowedUnit(unit, allowedUnits);
    const canonicalValue = toCanonical(input, unit, category);

    return {
      value: input,
      unit,
      canonicalValue,
      canonicalUnit,
      precision: displayPrecision,
    };
  }

  // Object with value and optional unit
  if (typeof input === 'object' && input !== null && 'value' in input) {
    const value = input.value as number;
    const unit = normalizeUnit((input.unit as string) || defaultUnit, category);
    validateAllowedUnit(unit, allowedUnits);
    const canonicalValue = toCanonical(value, unit, category);

    return {
      value,
      unit,
      canonicalValue,
      canonicalUnit,
      precision: displayPrecision,
    };
  }

  throw new Error(`Invalid unit input: ${JSON.stringify(input)}`);
}

/**
 * Process properties and convert unit type values to UnitValue objects
 *
 * @param properties - The properties object
 * @param schemaProps - The schema property definitions
 * @returns Properties with unit values converted to UnitValue objects
 */
export function processUnitProperties(
  properties: Record<string, unknown>,
  schemaProps: SchemaProperty[]
): Record<string, unknown> {
  const result = { ...properties };

  for (const prop of schemaProps) {
    if (prop.type === 'unit' && prop.unitConfig && properties[prop.name] !== undefined) {
      const rawValue = properties[prop.name];

      // Skip null values
      if (rawValue === null) {
        continue;
      }

      try {
        result[prop.name] = convertToUnitValue(
          rawValue as number | Partial<UnitValue>,
          prop.unitConfig
        );
      } catch (error) {
        // Let validation handle invalid values
        console.warn(`Failed to convert unit value for ${prop.name}:`, error);
      }
    }
  }

  return result;
}
